import { headerTabRoom, TabRoom } from 'const/room';
import {
  mergeRoomAssetToolbarUrl,
  parseRoomAssetToolbarParams,
  resolveRoomTableKey,
  roomAssetToolbarSliceEquals
} from 'app/pages/RoomPage/roomAssetToolbarUrlSync';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { usePaging } from 'hooks/usePaging';
import { SIZE_VALIDATION_ASSET_MODEL } from 'const/paging';
import {
  deleteAppendix,
  deleteAssetIssues,
  editAppendices,
  getAppendices,
  getAssetIssues,
  getLocationDetail,
  postAppendices
} from 'services/locations';
import { AssetIssues, Document, RoomInfo } from 'types/room';
import { submittedAssetsPhoto, tagAdditionalStructuresPhoto } from 'services/inspections';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { findIndex, fromPairs } from 'lodash';
import { FormGenerateReport } from '../../components/RoomPage/modalGenReport';
import { SizeOptionParams } from '../ValidationsPage/components/modules/AssetModel/hook';
import { deleteFileReport, getFileReport, getReport, ParamGetModal } from 'services/report';
import {
  buildCreateReportPayload,
  resolveReportRoomId,
  submitReportGeneration
} from 'utils/reportGeneration';
import { useLoading } from 'contexts/loading';
import { useFlash } from 'contexts/flash';
import { Appendix, Report } from 'types/report';
import { StorageServices } from 'services/storage';
import { getDocumentations } from 'services/documentation';
import { Documentation } from 'types/documents';
import { FormAddAppendix } from '../../components/RoomPage/modalAddAppendix';
import { AssetPoint } from '../../components/elements/ImageCustomSvg';
import { useGetIssues } from 'hooks/useGetOptionIssues';
import { useFetchAssetType } from 'hooks/useFetchAssetType';
import { useGetOptionAssetIssues } from 'hooks/useGetOptionAssetIssues';
import { useInfoRoom } from 'hooks/useInfoRoom';
import { useGetDocumentGenerate } from 'hooks/useGetDocumentGenerate';
import { SelectChangeEvent } from '@mui/material';
import { SearchType } from 'types/loctions';
import { useFilteredRoomContext } from 'hooks/useFilteredRoomContext';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
const storage = new StorageServices();

export const useInjection = () => {
  const { setFlash } = useFlash();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { clientId, locationId, clientName } = useParams();

  // Report Tab
  const [reports, setReports] = useState<Report[]>([]);
  const { documentGenerate } = useGetDocumentGenerate({ roomId: Number(locationId) });
  const {
    pagingInfo: pagingIssuesTab,
    setCurrentPage: setCurrentIssuesTab,
    setTotal: setTotalIssuesTab
  } = usePaging({
    rowsPerPage: SIZE_VALIDATION_ASSET_MODEL
  });

  const { optionAssetSelect } = useGetOptionAssetIssues(Number(locationId));

  const {
    optionAsset: optionFilterAssetType,
    hasMoreOptionAsset: hasMoreOptionType,
    handleFetchMoreOptionAsset: handleFetchMoreOptionType,
    getOptionAsset
  } = useFetchAssetType();

  const {
    optionIssues,
    hasMoreOptionIssues,
    fetchMoreIssues: handleFetchMoreIssues
  } = useGetIssues(Number(locationId));
  const { room, asyncHandleGetLocationLevelFive } = useInfoRoom(Number(locationId));

  /** Room Attributes → Assets / Issues / Archive: synced to ra_* URL params when main tab is Location Attributes. */
  const skipRaUrlHydrateRef = useRef(false);
  const [tabTable, setTabTableInner] = useState(0);
  const [roomAssetExpandedId, setRoomAssetExpandedId] = useState<number | undefined>(undefined);

  const { setLoading } = useLoading();
  const [assetIssues, setAssetIssues] = useState<AssetIssues[]>([]);
  const [valueFilterAssetType, setValueFilterAssetType] = useState<string>('');
  const [issuesStatus, setIssuesStatus] = useState<string>('');
  const [archiveFilter, setArchiveFilter] = useState<string>('active');
  const [archiveReportFilter, setArchiveReportFilter] = useState<string>('active');

  const [selectedCriticality, setSelectedCriticality] = useState('0');

  const [filterAssets, setFilterAssets] = useState<
    {
      key: string;
      value: string;
      label: string;
      level?: number;
    }[]
  >();

  const handleFilterAssets = useCallback(
    async (value?: { key: string; value: string; label: string; level?: number }) => {
      if (!value) return;

      setFilterAssets((prevState) => {
        let nextState: typeof prevState;

        if (!prevState) {
          nextState = [value];
        } else {
          const index = findIndex(prevState, { key: value.key });
          if (index !== -1) {
            const existingItem = prevState[index];
            if (
              (!value?.level && existingItem.value === value.value) ||
              Number(value?.level) === Number(existingItem?.level)
            ) {
              nextState = [...prevState.slice(0, index), ...prevState.slice(index + 1)];
            } else {
              const updated = [...prevState];
              updated[index] = value;
              nextState = updated;
            }
          } else {
            nextState = [...prevState, value];
          }
        }
        return nextState;
      });
    },
    []
  );
  const { systemDropdownValues: filteredOptions, singleEntryField: filteredSingleEntryField } =
    useFilteredRoomContext(clientId);

  const [roomInfo, setRoomInfo] = useState<RoomInfo>([]);

  const [page, setPage] = useState<{
    roomAttributes: number;
    documentation: number;
    issuesTab: number;
  }>({ roomAttributes: 1, documentation: 1, issuesTab: 1 });
  const [sortBy, setSortBy] = useState<{
    roomAttributes: string;
    documentation: string;
    issuesTab: string;
  }>({ roomAttributes: '', documentation: '', issuesTab: '' });
  const [order, setOrder] = useState<{
    roomAttributes: 'desc' | 'asc' | undefined;
    documentation: 'desc' | 'asc' | undefined;
    issuesTab: 'desc' | 'asc' | undefined;
  }>({
    roomAttributes: undefined,
    documentation: undefined,
    issuesTab: undefined
  });
  const [searchText, setSearchText] = useState<{
    roomAttributes: string;
    documentation: string;
    issuesTab: string;
  }>({
    roomAttributes: '',
    issuesTab: '',
    documentation: ''
  });
  // state option select
  const [documentFile, setDocumentFile] = useState<Document[]>([]);
  const [imageSelected, setImageSelected] = useState<{ id: number; key: string } | undefined>(
    undefined
  );

  const [maintainanceSelectedDate, setSelectedMaintainanceDate] = useState<string>();
  const [maintainanceSelectedType, setSelectedMaintainanceType] = useState<string>('All');
  const [searchType, setSearchType] = useState<SearchType>('partial-text');

  // START  - TABLE BOTTOM

  const {
    pagingInfo: pagingAssetIssues,
    setCurrentPage: setCurrentAssetIssues,
    setTotal: setTotalAssetIssues
  } = usePaging({
    rowsPerPage: SIZE_VALIDATION_ASSET_MODEL
  });

  //paging document tab
  const {
    pagingInfo: pagingDocument,
    setCurrentPage: setCurrentDocument,
    setTotal: setTotalDocument
  } = usePaging({
    rowsPerPage: SIZE_VALIDATION_ASSET_MODEL
  });

  const setTabTable = useCallback(
    (next: number) => {
      setTabTableInner(next);
      if (next !== 0) {
        setPage((prev) => ({ ...prev, roomAttributes: 1 }));
        setCurrentAssetIssues(1);
        setRoomAssetExpandedId(undefined);
      }
    },
    [setCurrentAssetIssues]
  );

  const roomAssetToolbarSlice = useMemo(
    () => ({
      subtab: tabTable,
      page: tabTable === 0 ? page.roomAttributes : 1,
      expandedSubmittedAssetId: tabTable === 0 ? roomAssetExpandedId : undefined
    }),
    [tabTable, page.roomAttributes, roomAssetExpandedId]
  );

  /** Browser back/forward: hydrate ra_* into React state (after Location Attributes load). */
  useLayoutEffect(() => {
    if (skipRaUrlHydrateRef.current) {
      skipRaUrlHydrateRef.current = false;
      return;
    }
    const mainTab = searchParams.get('tab') ?? TabRoom.RoomAttributes;
    if (mainTab !== TabRoom.RoomAttributes) return;

    const parsed = parseRoomAssetToolbarParams(searchParams);

    setTabTableInner((prev) => (prev === parsed.subtab ? prev : parsed.subtab));
    setRoomAssetExpandedId((prev) =>
      prev === parsed.expandedSubmittedAssetId ? prev : parsed.expandedSubmittedAssetId
    );
    /** Only Assets sub-tab has meaningful ra_page saved; don't overwrite stored page while on Issues/Archive */
    if (parsed.subtab === 0) {
      setPage((prev) =>
        prev.roomAttributes === parsed.page ? prev : { ...prev, roomAttributes: parsed.page }
      );
      setCurrentAssetIssues(parsed.page);
    }
  }, [searchParams, setCurrentAssetIssues]);

  /** Push toolbar slice to URL (share / reload / bookmark); skips noop merges like validations AV sync. */
  useEffect(() => {
    const mainTab = searchParams.get('tab') ?? TabRoom.RoomAttributes;
    if (mainTab !== TabRoom.RoomAttributes) return;

    setSearchParams(
      (prev) => {
        const fromUrl = parseRoomAssetToolbarParams(prev);
        if (roomAssetToolbarSliceEquals(roomAssetToolbarSlice, fromUrl)) return prev;
        skipRaUrlHydrateRef.current = true;
        return mergeRoomAssetToolbarUrl(prev, roomAssetToolbarSlice);
      },
      { replace: true }
    );
  }, [roomAssetToolbarSlice, searchParams, setSearchParams]);

  const assetParams = useMemo(() => {
    const tab = resolveRoomTableKey(tabTable, searchParams);
    const assetsTab = tab === 'issuesTab' ? 'issuesTab' : TabRoom.RoomAttributes;
    return {
      size: SIZE_VALIDATION_ASSET_MODEL,
      page: page[assetsTab],
      keyword: searchText[assetsTab] || undefined,
      orderBy: sortBy[assetsTab] || undefined,
      order: order[assetsTab] || undefined,
      criticality:
        selectedCriticality === '0' || !selectedCriticality ? undefined : selectedCriticality
    };
  }, [order, page, searchParams, searchText, sortBy, tabTable, selectedCriticality]);

  const documentParams = useMemo(
    () => ({
      size: SIZE_VALIDATION_ASSET_MODEL,
      page: page.documentation,
      keyword: searchText.documentation || undefined,
      orderBy: sortBy.documentation || undefined,
      order: order.documentation || undefined
    }),
    [order.documentation, page.documentation, searchText.documentation, sortBy.documentation]
  );

  const handleGetInitFileAsset = useCallback(async () => {
    const selectImage = imageSelected && { [imageSelected.key]: imageSelected.id };
    const filterParams = fromPairs(
      (filterAssets ?? [])
        .filter((item) => item.key && item.value)
        .map((item) => [item.key, item.value])
    );
    const dataAsset = await getAssetIssues({
      ...assetParams,
      roomId: locationId,
      archived: archiveFilter === 'archived',
      maintainanceType: maintainanceSelectedType === 'All' ? undefined : maintainanceSelectedType,
      maintainanceDate: maintainanceSelectedDate || undefined,
      searchType,
      ...selectImage,
      ...filterParams
    });
    setAssetIssues(dataAsset.submittedAssets);
    setTotalAssetIssues(dataAsset.total);
    return dataAsset.submittedAssets;
  }, [
    imageSelected,
    filterAssets,
    assetParams,
    locationId,
    archiveFilter,
    maintainanceSelectedType,
    maintainanceSelectedDate,
    setTotalAssetIssues,
    searchType
  ]);

  const { asyncCallback: asyncGetInitFileAsset } = useAsyncCallback(handleGetInitFileAsset, []);

  // handle when user choose image
  const handleSetImageSelected = useCallback(
    (value: { id: number; key: string }) => {
      const key = searchParams.get('tab') || TabRoom.RoomAttributes;
      if (key === TabRoom.RoomAttributes) {
        setCurrentAssetIssues(1);
      }
      if (key === TabRoom.Documentation) {
        setCurrentDocument(1);
      }
      setPage((prevState) => ({ ...prevState, [key]: 1 }));
      if (imageSelected?.id === value.id && imageSelected.key === value.key) {
        setImageSelected(undefined);
        return;
      }
      setImageSelected(value);
    },
    [imageSelected, searchParams, setCurrentAssetIssues, setCurrentDocument]
  );

  useEffect(() => {
    asyncGetInitFileAsset();
  }, [asyncGetInitFileAsset]);

  const handleGetInitLocations = useCallback(async () => {
    const data = (await getLocationDetail(Number(locationId))) as {
      location: Location;
      room: RoomInfo;
    };
    setRoomInfo(data.room);
  }, [locationId]);

  useEffect(() => {
    handleGetInitLocations();
  }, [handleGetInitLocations]);

  // get data document
  const handleGetInitDocument = useCallback(async () => {
    if (!locationId) return;

    const data = await getFileReport({ params: documentParams, roomId: Number(locationId) });
    setDocumentFile(data.documents);
    setTotalDocument(data.total);
    setCurrentDocument(documentParams.page);
  }, [locationId, documentParams, setTotalDocument, setCurrentDocument]);

  useEffect(() => {
    handleGetInitDocument();
  }, [
    order.documentation,
    page.documentation,
    searchText.documentation,
    sortBy.documentation,
    handleGetInitDocument
  ]);

  const onChangeTab = useCallback(
    (tab: number) => {
      const nameTab = headerTabRoom.find((item) => item.value === tab)?.param;
      const param = nameTab || TabRoom.RoomAttributes;
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (param === TabRoom.Documentation) {
          [...next.keys()].filter((k) => k.startsWith('ra_')).forEach((k) => next.delete(k));
        }
        next.set('tab', param);
        return next;
      });
    },
    [setSearchParams]
  );

  const onSearch = useCallback(
    (value: string) => {
      const key = resolveRoomTableKey(tabTable, searchParams);
      setSearchText((prevState) => ({ ...prevState, [key]: value }));
      setPage((prevState) => ({ ...prevState, [key]: 1 }));
      if (key === TabRoom.RoomAttributes) {
        setCurrentAssetIssues(1);
      }
      if (key === TabRoom.Documentation) {
        setCurrentDocument(1);
      }
      if (key === 'issuesTab') {
        setCurrentIssuesTab(1);
      }
    },
    [tabTable, searchParams, setCurrentAssetIssues, setCurrentDocument, setCurrentIssuesTab]
  );

  const sortOption = useMemo(() => {
    const key = resolveRoomTableKey(tabTable, searchParams);
    return {
      field: sortBy[key],
      order: order[key]
    };
  }, [tabTable, order, searchParams, sortBy]);

  const handleSort = useCallback(
    (orderBy: string) => {
      const key = resolveRoomTableKey(tabTable, searchParams);
      const sortByNow = sortBy[key];
      const orderNow = order[key];
      if (orderBy === sortByNow) {
        if (orderNow === 'asc') {
          setOrder((prevState) => ({ ...prevState, [key]: 'desc' }));
        } else {
          setOrder((prevState) => ({ ...prevState, [key]: 'asc' }));
        }
      } else {
        setSortBy((prevState) => ({ ...prevState, [key]: orderBy }));
        setOrder((prevState) => ({ ...prevState, [key]: 'asc' }));
      }
    },
    [tabTable, searchParams, sortBy, order]
  );

  const handleChangePage = useCallback(
    (value: number) => {
      skipRaUrlHydrateRef.current = true;
      const key = resolveRoomTableKey(tabTable, searchParams);
      if (key === TabRoom.RoomAttributes) {
        setCurrentAssetIssues(value);
      }
      if (key === TabRoom.Documentation) {
        setCurrentDocument(value);
      }
      if (key === 'issuesTab') {
        setCurrentIssuesTab(value);
      }
      setPage((prevState) => ({ ...prevState, [key]: value }));
    },
    [tabTable, searchParams, setCurrentAssetIssues, setCurrentDocument, setCurrentIssuesTab]
  );
  // END  - TABLE BOTTOM
  const paths: RoomInfo = useMemo(() => {
    if (!(roomInfo?.length >= 1)) return [];
    return [
      { name: 'Clients', level: 999, onClick: () => navigate('/clients') },
      {
        name: clientName || '',
        id: Number(clientId),
        isClient: true,
        level: 998,
        onClick: () => navigate(`/clients/${clientName}/${clientId}/location`)
      },
      ...roomInfo
        .sort((a, b) => a.level - b.level)
        .map((item) => {
          if (item.level === 5) {
            return {
              ...item
            };
          }
          return {
            ...item,
            onClick: () => navigate(`/clients/${clientName}/${clientId}/location/${item.id}`)
          };
        })
    ];
  }, [clientId, clientName, navigate, roomInfo]);

  useEffect(() => {
    if (clientId && clientName && locationId) {
      storage.setLastRoomPath(
        `/clients/${clientName}/${clientId}/location/${locationId}/room`,
        clientId
      );
    }
  }, [clientId, clientName, locationId]);
  const updateImageAsset = useCallback(
    async ({
      submittedPhotoId,
      additionalPhotoId,
      submitId,
      file,
      assets
    }: {
      submitId: number;
      submittedPhotoId?: number;
      additionalPhotoId?: number;
      file?: File;
      assets: AssetPoint[];
    }) => {
      const formData = new FormData();
      if (file) {
        formData.append('file', file);
      }
      formData.append('submittedAssets', JSON.stringify(assets));
      if (submittedPhotoId) {
        await submittedAssetsPhoto({ submittedPhotoId, submitId, formData });
        await asyncHandleGetLocationLevelFive(room?.roomId);
        return;
      }
      if (additionalPhotoId) {
        await tagAdditionalStructuresPhoto({
          additionalPhotoId,
          submitId,
          formData
        });
        await asyncHandleGetLocationLevelFive(room?.roomId);
        return;
      }
    },
    [asyncHandleGetLocationLevelFive, room?.roomId]
  );

  const { asyncCallback: asyncUpdateImageAsset } = useAsyncCallback(updateImageAsset, []);

  // modal Generate Report
  const [openModalGenerateReport, setOpenModalGenerateReport] = useState<boolean>(false);

  const onCloseModalGenerateReport = useCallback(() => {
    setOpenModalGenerateReport(false);
  }, []);
  const onSubmitModalGenerateReport = useCallback(
    async (value: FormGenerateReport) => {
      onCloseModalGenerateReport();
      setLoading(true);
      try {
        const roomLocationId = resolveReportRoomId(locationId, room?.roomId);
        const convertPayload = buildCreateReportPayload(value, roomLocationId);
        const ok = await submitReportGeneration(convertPayload, setFlash);
        if (ok) {
          await handleGetInitDocument();
        }
      } finally {
        setLoading(false);
      }
    },
    [
      onCloseModalGenerateReport,
      room?.roomId,
      locationId,
      handleGetInitDocument,
      setFlash,
      setLoading
    ]
  );

  const onFilterAssetType = useCallback((type: string) => {
    setValueFilterAssetType(type);
  }, []);

  const [deleteAsset, setDeleteAsset] = useState<AssetIssues>();

  const onDeleteAsset = useCallback(async () => {
    if (!deleteAsset?.id || !deleteAsset?.submitId) return;
    setDeleteAsset(undefined);
    await deleteAssetIssues({ assetId: deleteAsset?.id, submitId: deleteAsset?.submitId });
    await handleGetInitFileAsset();
    setFlash({ type: 'success', message: 'Delete Asset successful' });
  }, [deleteAsset?.id, deleteAsset?.submitId, handleGetInitFileAsset, setFlash]);

  const { asyncCallback: asyncOnDeleteAsset } = useAsyncCallback(onDeleteAsset, []);

  useEffect(() => {
    const paramsConvert = {
      page: 1,
      size: SizeOptionParams
    };
    getOptionAsset(paramsConvert);
  }, [getOptionAsset]);

  const handleGetInitReport = useCallback(
    async (paramsProps: Partial<ParamGetModal>) => {
      const selectImage = imageSelected && { [imageSelected.key]: imageSelected.id };
      const data = await getReport({ ...paramsProps, ...selectImage, roomId: locationId });
      setReports(data.reports);
      setTotalIssuesTab(data.total);
    },
    [imageSelected, locationId, setTotalIssuesTab]
  );

  const { asyncCallback: asyncHandleGetInitReport } = useAsyncCallback(handleGetInitReport, []);
  useEffect(() => {
    asyncHandleGetInitReport({
      size: SIZE_VALIDATION_ASSET_MODEL,
      page: page?.issuesTab,
      keyword: searchText?.issuesTab || undefined,
      orderBy: sortBy?.issuesTab || undefined,
      order: order?.issuesTab || undefined,
      archived: archiveReportFilter === 'archived',
      status: issuesStatus || undefined,
      searchType
    });
  }, [
    imageSelected,
    asyncHandleGetInitReport,
    page?.issuesTab,
    searchText?.issuesTab,
    sortBy?.issuesTab,
    order?.issuesTab,
    archiveReportFilter,
    issuesStatus,
    searchType
  ]);

  // state modal new appendix
  const [optionFilePdf, setOptionFilePdf] = useState<{ label: string; value: string }[]>([]);
  const [hasMoreOptionFilePdf, setHasMoreOptionFilePdf] = useState<boolean>(false);
  const [pageOptionFilePdf, setPageOptionFilePdf] = useState<number>(1);
  const [isLoadingMoreFilePdf, setIsLoadingMoreFilePdf] = useState<boolean>(false);

  // state modal view appendix
  const [optionAppendix, setOptionAppendix] = useState<Appendix[]>([]);
  const [hasMoreOptionAppendix, setHasMoreOptionAppendix] = useState<boolean>(false);
  const [pageOptionAppendix, setPageOptionAppendix] = useState<number>(1);
  const [isLoadingMoreAppendix, setIsLoadingMoreAppendix] = useState<boolean>(false);
  const [idDeleteAppendices, setIdDeleteAppendices] = useState<number | null>(null);
  const [itemEditAppendices, setItemEditAppendices] = useState<Appendix | null>(null);
  const [openModalViewAllAppendix, setOpenModalViewAllAppendix] = useState<boolean>(false);
  const [openModalAddAppendix, setOpenModalAddAppendix] = useState<boolean>(false);

  const onCloseModalAddAppendix = useCallback(() => {
    setOpenModalAddAppendix(false);
  }, []);

  const handleGetInitDocumentAddpendix = useCallback(async () => {
    if (!locationId) return;

    const data = await getDocumentations({
      page: 1,
      size: SizeOptionParams
    });
    setOptionFilePdf(
      data?.documentations.map((item: Documentation) => {
        return {
          value: item?.id?.toString(),
          label: item?.storage.fileName
        };
      })
    );
    if (data.total <= SizeOptionParams) {
      setHasMoreOptionFilePdf(false);
      return;
    }
    setHasMoreOptionFilePdf(true);
    setPageOptionFilePdf(2);
  }, [locationId]);

  useEffect(() => {
    handleGetInitDocumentAddpendix();
  }, [handleGetInitDocumentAddpendix]);

  const handleFetchMoreFilePdf = useCallback(async () => {
    if (!hasMoreOptionFilePdf || isLoadingMoreFilePdf) return;
    try {
      const paramsConvert = {
        page: pageOptionFilePdf,
        size: SizeOptionParams
      };
      setIsLoadingMoreFilePdf(true);
      const data = await getDocumentations({
        ...paramsConvert
      });

      const dataConvert = data?.documentations.map((item: Documentation) => ({
        value: item.id.toString(),
        label: item.storage.fileName
      }));
      setOptionFilePdf([...optionFilePdf, ...dataConvert]);
      if (data.total <= SizeOptionParams * pageOptionFilePdf) {
        setHasMoreOptionFilePdf(false);
        return;
      }
      setHasMoreOptionFilePdf(true);
      setPageOptionFilePdf(pageOptionFilePdf + 1);
    } finally {
      setHasMoreOptionFilePdf(false);
    }
  }, [hasMoreOptionFilePdf, isLoadingMoreFilePdf, optionFilePdf, pageOptionFilePdf]);

  const handleGetListDocument = useCallback(async () => {
    if (!locationId) return;
    const data = await getAppendices(locationId, { page: 1, size: SizeOptionParams });
    setOptionAppendix(data?.appendices);
    if (data.total <= SizeOptionParams) {
      setPageOptionAppendix(1);
      setHasMoreOptionAppendix(false);
      return;
    }
    setHasMoreOptionAppendix(true);
    setPageOptionAppendix(2);
  }, [locationId]);

  useEffect(() => {
    handleGetListDocument();
  }, [handleGetListDocument]);

  const handleFetchMoreAppendix = useCallback(async () => {
    if (!hasMoreOptionAppendix || isLoadingMoreAppendix || !locationId) return;
    try {
      const paramsConvert = {
        page: pageOptionAppendix,
        size: SizeOptionParams
      };
      setIsLoadingMoreAppendix(true);
      const data = await getAppendices(locationId, {
        ...paramsConvert
      });

      setOptionAppendix([...optionAppendix, ...data.appendices]);
      if (data.total <= SizeOptionParams * pageOptionAppendix) {
        setHasMoreOptionAppendix(false);
        return;
      }
      setHasMoreOptionAppendix(true);
      setPageOptionAppendix(pageOptionAppendix + 1);
    } finally {
      setIsLoadingMoreAppendix(false);
    }
  }, [
    hasMoreOptionAppendix,
    isLoadingMoreAppendix,
    locationId,
    optionAppendix,
    pageOptionAppendix
  ]);

  const handleDeleteAppendix = useCallback(async () => {
    if (!locationId || !idDeleteAppendices) return;
    await deleteAppendix(locationId, idDeleteAppendices);
    setFlash({
      type: 'success',
      message: `Delete Appendix ${idDeleteAppendices} successful` || ''
    });
    setIdDeleteAppendices(null);
    setOptionAppendix((prevState) => prevState.filter((item) => item.id !== idDeleteAppendices));
  }, [idDeleteAppendices, locationId, setFlash]);

  const { asyncCallback: asyncHandleDeleteAppendix } = useAsyncCallback(handleDeleteAppendix, []);

  const onSubmitModalEditAppendix = useCallback(
    async (value: FormAddAppendix) => {
      if (!locationId || !itemEditAppendices?.id) return;
      setItemEditAppendices(null);
      const payload = {
        content: value?.content,
        documentId: value?.fileId
      };
      const data = await editAppendices(itemEditAppendices?.id, locationId, payload);
      setFlash({ type: 'success', message: data.message || '' });
      await handleGetListDocument();
    },
    [itemEditAppendices?.id, locationId, setFlash, handleGetListDocument]
  );

  const { asyncCallback: asyncSubmitModalEditAppendix } = useAsyncCallback(
    onSubmitModalEditAppendix,
    []
  );

  const onSubmitModalAddAppendix = useCallback(
    async (value: FormAddAppendix) => {
      if (!locationId) return;
      onCloseModalAddAppendix();
      const payload = {
        content: value?.content,
        documentIds: value?.fileIds?.map((item) => Number(item))
      };
      const data = await postAppendices(locationId, payload);
      setFlash({ type: 'success', message: data.message || '' });
      await handleGetListDocument();
    },
    [locationId, onCloseModalAddAppendix, setFlash, handleGetListDocument]
  );

  const { asyncCallback: asyncSubmitModalAddAppendix } = useAsyncCallback(
    onSubmitModalAddAppendix,
    []
  );

  const onClickLinkModalAddAppendix = useCallback(() => {
    setOpenModalViewAllAppendix(true);
  }, []);

  const handleDeleteDocument = useCallback(
    async (id: number) => {
      if (!confirm('Are you sure you want to delete this report?')) return;
      await deleteFileReport(id);
      setDocumentFile((prevState) => prevState.filter((item) => item.id !== id));
      setFlash({
        type: 'success',
        message: `Delete document ${id} successful` || ''
      });
    },
    [setFlash]
  );

  const { asyncCallback: asyncDeleteDocument } = useAsyncCallback(handleDeleteDocument, []);

  // start inspection
  const [openAssetForm, setOpenAssetForm] = useState<boolean>(false);
  const [openInspection, setOpenInspection] = useState<boolean>(false);

  return {
    // === Paging, Params, Filter ===
    searchParams,
    assetParams,
    documentParams,
    tab: headerTabRoom.find((item) => item.param === searchParams.get('tab'))?.value || 0,
    archiveFilter,
    setArchiveFilter,
    archiveReportFilter,
    setArchiveReportFilter,

    // === Tabs & Table View ===
    onChangeTab,
    tabTable,
    setTabTable,
    roomAssetExpandedId,
    setRoomAssetExpandedId,

    // === Search & Sort ===
    searchText,
    onSearch,
    handleSort,
    sortOption,

    // === Paging ===
    pagingAssetIssues,
    pagingDocument,
    pagingIssuesTab,

    // === Asset Issues ===
    assetIssues,
    setAssetIssues,
    optionIssues,
    handleFetchMoreIssues: handleFetchMoreIssues,
    hasMoreIssues: hasMoreOptionIssues,
    handleChangePage,
    setTotalAssetIssues,
    handleDeleteAsset: (value: AssetIssues) => setDeleteAsset(value),
    deleteAsset,
    setDeleteAsset,
    onDeleteAsset: asyncOnDeleteAsset,
    issuesStatus,
    setIssuesStatus,

    // === Room ===
    room,
    paths,

    // === Image Asset Handling ===
    asyncUpdateImageAsset,
    asyncGetInitFileAsset,
    handleSetImageSelected,
    imageSelected,

    // === Filter Asset Type ===
    onFilterAssetType,
    valueFilterAssetType,
    optionFilterAssetType,
    hasMoreOptionType,
    handleFetchMoreOptionType,
    optionAssetSelect,

    // === Documents ===
    documentFile,

    // === Documents Generate===
    documentGenerate,

    // === Reports ===
    reports,
    setReports,
    openModalGenerateReport,
    setOpenModalGenerateReport,
    onCloseModalGenerateReport,
    onSubmitModalGenerateReport,

    // === Appendices ===
    itemEditAppendices,
    setItemEditAppendices,
    handleDeleteAppendix: asyncHandleDeleteAppendix,
    handleFetchMoreAppendix,
    idDeleteAppendices,
    setIdDeleteAppendices,
    asyncSubmitModalEditAppendix,
    optionAppendix,
    hasMoreOptionAppendix,

    // === PDF/File Selection ===
    optionFilePdf,
    hasMoreOptionFilePdf,
    handleFetchMoreFilePdf,

    // === Modals (Appendices) ===
    openModalAddAppendix,
    setOpenModalAddAppendix,
    openModalViewAllAppendix,
    setOpenModalViewAllAppendix,
    onClickLinkModalAddAppendix,
    onCloseModalAddAppendix,
    onSubmitModalAddAppendix: asyncSubmitModalAddAppendix,
    frequencyOptions: filteredOptions,
    filteredSingleEntryField,
    onUpdateRoom: () => asyncHandleGetLocationLevelFive(room?.roomId),
    selectedCriticality,
    onFilterCriticality: useCallback((e: SelectChangeEvent<unknown>) => {
      setSelectedCriticality(e.target.value as string);
    }, []),
    handleFilterAssets,
    filterAssets,
    maintainanceSelectedDate,
    setSelectedMaintainanceDate,
    maintainanceSelectedType,
    setSelectedMaintainanceType,
    onRefreshReportIssue: useCallback(() => {
      asyncHandleGetInitReport({
        size: SIZE_VALIDATION_ASSET_MODEL,
        page: page?.issuesTab,
        keyword: searchText?.issuesTab || undefined,
        orderBy: sortBy?.issuesTab || undefined,
        order: order?.issuesTab || undefined,
        archived: archiveReportFilter === 'archived',
        status: issuesStatus || undefined
      });
    }, [
      archiveReportFilter,
      asyncHandleGetInitReport,
      issuesStatus,
      order?.issuesTab,
      page?.issuesTab,
      searchText?.issuesTab,
      sortBy?.issuesTab
    ]),
    searchType,
    setSearchType,
    asyncDeleteDocument,
    // start inspection
    openInspection,
    setOpenInspection,
    openAssetForm,
    setOpenAssetForm,
    roomInfo,
    asyncHandleGetLocationLevelFive,
    onRefreshData: handleGetInitLocations,
    onRefreshDataAssets: handleGetInitFileAsset,
    roomDetail: roomInfo?.find((item) => item.level === 5)
  };
};
